/**
 * The minimum-drawdown check for an account-based pension, as pure logic.
 *
 * WHY IT IS EXTRACTED (owner-reported, 2026-08-31). The warning fired for the client's ABP and not
 * the partner's, and the reason was invisible: the check needs the OWNER'S AGE, that age comes from
 * the owner's date of birth, and when the date of birth is missing `calculateAge` returns null — at
 * which point the component returned null too. No warning, no explanation, nothing on screen to say
 * the check had not been made. The client sees a payment we never verified and assumes we did.
 *
 * So the check now has THREE outcomes rather than two, and "we could not tell" is one of them. A
 * check that cannot run must say so; silence is indistinguishable from a pass.
 *
 * Pure and exported so it can be tested without rendering — the frontend test environment is `node`,
 * with no DOM, and this rule is worth a test either way: the owner asked for one so it cannot break
 * again unnoticed.
 */
import { MINIMUM_PENSION_DRAWDOWN_RATES } from '../constants/index.ts';
import { calculateAge } from '../utils/index.ts';

export interface AbpOwnerContext {
    client?: { firstName?: string; dob?: string } | null;
    partner?: { firstName?: string; dob?: string } | null;
}

/**
 * Whose age governs this pension, and what that age is.
 *
 * Super is always individually owned, so an account-based pension is the client's or the partner's.
 * Anything else ('joint' on legacy data, or an entity) leaves us unable to name an owner — which is
 * reported rather than quietly treated as the client's.
 */
export function abpOwnerAge(
    ownership: string | undefined | null,
    people: AbpOwnerContext,
): { age: number | null; who: string } {
    if (ownership === 'client') {
        return { age: calculateAge(people.client?.dob), who: people.client?.firstName || 'the applicant' };
    }
    if (ownership === 'partner') {
        return { age: calculateAge(people.partner?.dob), who: people.partner?.firstName || 'their partner' };
    }
    return { age: null, who: 'the owner' };
}

/** The legislated minimum drawdown rate for an age. */
export function minimumDrawdownRate(age: number | null | undefined): number {
    if (!age || age <= 0) return 0;
    const bracket = MINIMUM_PENSION_DRAWDOWN_RATES.find((b) => age <= b.age);
    return bracket ? bracket.rate : 0;
}

export type PensionMinimumState =
    /** Nothing to check yet — no balance, or no payment entered. */
    | { state: 'not-applicable' }
    /** We have a balance and a payment but cannot work out the owner's age. */
    | { state: 'unknown-age'; who: string }
    /** The payment meets or exceeds the minimum. */
    | { state: 'ok'; minimumPayment: number; rate: number; age: number }
    /** The payment is below the legislated minimum. */
    | { state: 'below'; minimumPayment: number; rate: number; age: number; shortfall: number };

/**
 * Check one account-based pension.
 *
 * `value` and `income` are taken as raw fact-find values because that is what the form holds
 * mid-edit — strings, blanks and all.
 */
export function checkPensionMinimum(
    value: unknown,
    income: unknown,
    ownerAge: number | null | undefined,
    who = 'the owner',
): PensionMinimumState {
    const balance = parseFloat(String(value ?? '')) || 0;
    const payment = parseFloat(String(income ?? '')) || 0;
    if (balance <= 0 || payment <= 0) return { state: 'not-applicable' };

    /*
     * THE CASE THAT USED TO VANISH. A balance and a payment are present, so the client plainly
     * expects the check to run — but with no date of birth there is no age and no minimum. Saying so
     * is the only honest answer, and it points at the fix.
     */
    if (!ownerAge || ownerAge <= 0) return { state: 'unknown-age', who };

    const rate = minimumDrawdownRate(ownerAge);
    const minimumPayment = balance * rate;
    // A cent of tolerance: a payment set exactly at the minimum must not read as a breach.
    if (payment < minimumPayment - 0.01) {
        return { state: 'below', minimumPayment, rate, age: ownerAge, shortfall: minimumPayment - payment };
    }
    return { state: 'ok', minimumPayment, rate, age: ownerAge };
}
